<template>
    <div class="file-preview">
        <!-- Loader -->
        <div v-if="loading" class="text-center my-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Chargement...</span>
            </div>
            <p class="mt-2">Chargement des fichiers...</p>
        </div>

        <!-- Aucun fichier -->
        <div v-else-if="!loading && documents.length === 0" class="text-center my-5">
            <i class="bi bi-file-ear-minus display-4 text-muted"></i>
            <p class="text-muted mt-2">Aucun fichier joint</p>
        </div>

        <!-- Liste des fichiers -->
        <div v-else class="documents-list">
            <div class="card mb-3">
                <div class="card-header bg-light">
                    <h6 class="mb-0">
                        <i class="bi bi-paperclip me-2"></i>
                        Pièces jointes ({{ documents.length }})
                    </h6>
                </div>
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table table-hover mb-0">
                            <thead class="table-light">
                                <tr>
                                    <th width="50"></th>
                                    <th>Nom du fichier</th>
                                    <th>Taille</th>
                                    <th>Date d'ajout</th>
                                    <th class="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="doc in documents" :key="doc.id">
                                    <td>
                                        <!-- Icône selon le type -->
                                        <i v-if="doc.is_image" class="bi bi-file-ear-image text-primary fs-5"></i>
                                        <i v-else-if="doc.is_pdf" class="bi bi-file-ear-pdf text-danger fs-5"></i>
                                        <i v-else-if="doc.extension === 'doc' || doc.extension === 'docx'" class="bi bi-file-ear-word text-primary fs-5"></i>
                                        <i v-else-if="doc.extension === 'xls' || doc.extension === 'xlsx'" class="bi bi-file-ear-excel text-success fs-5"></i>
                                        <i v-else class="bi bi-file-ear text-secondary fs-5"></i>
                                    </td>
                                    <td>
                                        <div class="fw-semibold">{{ doc.name }}</div>
                                        <small class="text-muted">.{{ doc.extension }}</small>
                                    </td>
                                    <td>
                                        <span class="badge bg-light text-dark">{{ doc.full_size }}</span>
                                    </td>
                                    <td>
                                        <small class="text-muted">{{ formatDate(doc.created_at) }}</small>
                                    </td>
                                    <td class="text-end">
                                        <div class="btn-group btn-group-sm">
                                            <!-- Prévisualiser (images et PDF) -->
                                            <button
                                                v-if="doc.is_image || doc.is_pdf"
                                                @click="previewDocument(doc)"
                                                class="btn btn-outline-primary"
                                                title="Prévisualiser"
                                            >
                                                <i class="bi bi-eye"></i>
                                            </button>

                                            <!-- Télécharger -->
                                            <button
                                                @click="downloadDocument(doc)"
                                                class="btn btn-outline-success"
                                                title="Télécharger"
                                            >
                                                <i class="bi bi-download"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal de prévisualisation -->
        <div v-if="showPreviewModal" class="modal fade" tabindex="-1" aria-labelledby="previewModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="previewModalLabel">
                            <i class="bi bi-eye me-2"></i>
                            {{ currentDocument?.name }}
                        </h5>
                        <button type="button" class="btn-close" @click="closePreview" aria-label="Close"></button>
                    </div>
                    <div class="modal-body bg-light p-0 text-center">
                        <div v-if="previewLoading" class="py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Chargement...</span>
                            </div>
                        </div>
                        <iframe
                            v-else
                            :src="previewUrl"
                            class="preview-frame"
                            frameborder="0"
                        ></iframe>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closePreview">
                            Fermer
                        </button>
                        <button type="button" class="btn btn-primary" @click="downloadCurrentDocument">
                            <i class="bi bi-download me-2"></i>
                            Télécharger
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backend from "@/utils/backend"

export default {
    name: 'FilePreview',
    props: {
        complaintId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            loading: true,
            documents: [],
            showPreviewModal: false,
            currentDocument: null,
            previewLoading: false,
            previewUrl: null
        }
    },
    mounted() {
        this.loadDocuments()
    },
    methods: {
        async loadDocuments() {
            this.loading = true
            try {
                const url = `complaints/${this.complaintId}/documents`
                const { data } = await backend({ url })
                this.documents = data.data || []
            } catch (error) {
                console.error('Erreur lors du chargement des documents:', error)
                // Afficher un message d'erreur à l'utilisateur
                this.$toast?.error('Impossible de charger les fichiers joints')
            } finally {
                this.loading = false
            }
        },

        async previewDocument(doc) {
            this.currentDocument = doc
            this.showPreviewModal = true
            this.previewLoading = true

            try {
                // Pour les images et PDF, on peut utiliser directement l'URL
                this.previewUrl = doc.preview_url + '?token=' + localStorage.getItem('token')

                // Alternative : utiliser le endpoint preview
                // const url = `complaints/${this.complaintId}/documents/${doc.id}/preview`
                // this.previewUrl = url + '?token=' + localStorage.getItem('token')
            } catch (error) {
                console.error('Erreur lors de la prévisualisation:', error)
                this.$toast?.error('Impossible de prévisualiser ce fichier')
            } finally {
                this.previewLoading = false
            }
        },

        async downloadDocument(doc) {
            try {
                let downloadUrl;

                if (doc.source === 'old') {
                    // Ancien système - téléchargement direct
                    downloadUrl = doc.download_url;
                } else {
                    // Nouveau système - avec token
                    downloadUrl = doc.download_url + '?token=' + localStorage.getItem('token')
                }

                // Ouvrir dans un nouvel onglet
                window.open(downloadUrl, '_blank')

                this.$toast?.success('Téléchargement démarré')
            } catch (error) {
                console.error('Erreur lors du téléchargement:', error)
                this.$toast?.error('Impossible de télécharger ce fichier')
            }
        },

        async downloadCurrentDocument() {
            if (this.currentDocument) {
                await this.downloadDocument(this.currentDocument)
            }
        },

        closePreview() {
            this.showPreviewModal = false
            this.currentDocument = null
            this.previewUrl = null
        },

        formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }
    },
    watch: {
        complaintId() {
            this.loadDocuments()
        }
    }
}
</script>

<style scoped>
.documents-list {
    max-width: 100%;
}

.preview-frame {
    width: 100%;
    min-height: 600px;
    border: none;
}

.file-preview {
    background: white;
    border-radius: 8px;
}

.table th {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
}

.btn-group-sm .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 768px) {
    .table-responsive {
        font-size: 0.875rem;
    }

    .preview-frame {
        min-height: 400px;
    }
}
</style>
